import React from 'react';
import FilterableDataTable from '../../../components/ui/FilterableDataTable';
import { FeatureGate } from '../../../contexts/FeatureFlagContext';
import { table, cn } from '../../../components/ui/uiTheme';
import { SectionSubtitle } from '../../../components/ui/PageLayout';

export default function RepositoryBranchesTab({
  repositoryId,
  tabBranches,
  tabBranchesStats,
  tabBranchesLoading,
  branchesColumns,
}: any) {
  return (
    <div
      role="tabpanel"
      id="repo-tabpanel-branches"
      aria-labelledby="repo-tab-branches"
      className="section-block-mdl"
    >
      <FeatureGate flag="ui.repositoryDetail.branchesTab">
        <div className={cn(table.wrapper, 'overflow-hidden')}>
          <div className="px-4 py-3 border-b border-dark-border">
            <SectionSubtitle>Branches</SectionSubtitle>
            <div className="mt-0.5 flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
              <p className="text-sm text-slate-400 sm:min-w-0 sm:flex-1">
                Stored from last sync. Use <strong>Sync Now</strong> above to refresh from GitHub.
                {Array.isArray(tabBranchesStats?.commitSyncBranches) &&
                  tabBranchesStats.commitSyncBranches.length > 0 && (
                  <>
                    {' '}
                    <span className="text-slate-300">
                      Commit rows in the database are synced for{' '}
                      <strong>{tabBranchesStats.commitSyncBranches.join(', ')}</strong>; the Stored
                      commits column counts those rows.
                    </span>
                  </>
                )}
              </p>
              {tabBranchesStats != null && (
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-400 shrink-0 sm:justify-end">
                  <span>Total: {tabBranchesStats.total ?? tabBranches.length}</span>
                  {tabBranchesStats.protectedBranches != null && (
                    <span>Protected: {tabBranchesStats.protectedBranches}</span>
                  )}
                  {tabBranchesStats.stale != null && (
                    <span>Stale (30+ days): {tabBranchesStats.stale}</span>
                  )}
                </div>
              )}
            </div>
          </div>
          <FilterableDataTable
            key={`branches-${repositoryId}`}
            data={tabBranches}
            columns={branchesColumns}
            getRowId={(row) => String(row.id)}
            isLoading={tabBranchesLoading}
            emptyMessage="No branches in database. Sync the repository to fetch and store branches from GitHub."
            searchPlaceholder="Search branches…"
            initialSorting={[{ id: 'name', desc: false }]}
            renderFilterPanel={({ close, columnFilters, setColumnFilters }) => {
              const cur = columnFilters.find((f) => f.id === 'branchFilters')?.value ?? {
                defaultOnly: false,
                protectedOnly: false,
                staleOnly: false,
              };
              const setBranchFlags = (patch) => {
                setColumnFilters((prev) => {
                  const existing = prev.find((f) => f.id === 'branchFilters')?.value ?? {
                    defaultOnly: false,
                    protectedOnly: false,
                    staleOnly: false,
                  };
                  const next = { ...existing, ...patch };
                  const active = next.defaultOnly || next.protectedOnly || next.staleOnly;
                  const rest = prev.filter((f) => f.id !== 'branchFilters');
                  if (!active) return rest;
                  return [...rest, { id: 'branchFilters', value: next }];
                });
              };
              return (
                <div className="space-y-4">
                  <p className="text-sm text-slate-400">
                    Selected options combine with <strong>AND</strong> (branch must match every
                    checked rule).
                  </p>
                  <label className="flex items-center gap-2 text-sm text-slate-100 cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4 shrink-0 rounded border-dark-border bg-dark-elevated"
                      checked={!!cur.defaultOnly}
                      onChange={(e) => setBranchFlags({ defaultOnly: e.target.checked })}
                    />
                    Default branch only
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-100 cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4 shrink-0 rounded border-dark-border bg-dark-elevated"
                      checked={!!cur.protectedOnly}
                      onChange={(e) => setBranchFlags({ protectedOnly: e.target.checked })}
                    />
                    Protected only
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-100 cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4 shrink-0 rounded border-dark-border bg-dark-elevated"
                      checked={!!cur.staleOnly}
                      onChange={(e) => setBranchFlags({ staleOnly: e.target.checked })}
                    />
                    Stale only
                  </label>
                  <div className="flex justify-end">
                    <button type="button" className="btn-secondary-mdl-sm" onClick={close}>
                      Done
                    </button>
                  </div>
                </div>
              );
            }}
          />
        </div>
      </FeatureGate>
    </div>
  );
}
